import React from 'react';
import { RatingMode, RatingValues, CategoryControlConfig } from '../types';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../../ui/components/Tabs';
import { QuickRateControl } from './QuickRateControl';
import { DotRatingControl } from './DotRatingControl';
import { CenteredDotRatingControl } from './CenteredDotRatingControl';
import { clampRating, getSortedCategoryConfigs, filterApplicableCategories } from '../utils';

interface CoffeeRatingSectionProps {
    // mode control
    mode: RatingMode;
    onModeChange: (mode: RatingMode) => void;

    // quick rate
    quickRate?: number;
    onQuickRateChange: (value: number) => void;

    // detailed ratings
    coffeeType: string;
    coffeeTypesCache: Record<string, {
        rateCategories: { [key: string]: { weight: number; required: boolean } };
    }>;
    rating: RatingValues;
    onRatingChange: (category: string, value: number) => void;

    // centered slider positions (for categories like acidity)
    centeredSliderPositions?: Record<string, number>;
    onCenteredSliderChange?: (category: string) => (rating: number, position: number) => void;

    // centered category configuration
    centeredCategories?: {
        [category: string]: {
            leftLabel: string;
            rightLabel: string;
            maxRange?: number;
        };
    };

    // extra categories not defined in backend (e.g. temperature for hot coffees)
    extraCategories?: { [key: string]: { weight: number; required: boolean } };

    // chosen coffee's coffeeFormat — drives conditional hiding of sugar/milk sliders
    coffeeFormat?: { sugar?: unknown; milk?: unknown } | null;
}

export const CoffeeRatingSection: React.FC<CoffeeRatingSectionProps> = ({
    mode,
    onModeChange,
    quickRate,
    onQuickRateChange,
    coffeeType,
    coffeeTypesCache,
    rating,
    onRatingChange,
    centeredSliderPositions = {},
    onCenteredSliderChange,
    centeredCategories,
    extraCategories,
    coffeeFormat,
}) => {
    // merge backend categories with any extra frontend-provided categories
    const mergedCategories = coffeeType && coffeeTypesCache[coffeeType]
        ? { ...coffeeTypesCache[coffeeType].rateCategories, ...(extraCategories || {}) }
        : extraCategories || {};

    // drop sugar/milk sliders when the chosen coffee's format omits them
    const applicableCategories = filterApplicableCategories(mergedCategories, coffeeFormat ?? null);

    const categoryConfigs: CategoryControlConfig[] = Object.keys(applicableCategories).length > 0
        ? getSortedCategoryConfigs(applicableCategories, centeredCategories)
        : [];

    const handleRatingChange = (category: string, value: number) => {
        const clamped = clampRating(value, 'decimal');
        onRatingChange(category, clamped);
    };

    const handleCenteredChange = (category: string) => (rating: number, position: number) => {
        const clamped = clampRating(rating, 'decimal');
        onRatingChange(category, clamped);
        if (onCenteredSliderChange) {
            onCenteredSliderChange(category)(clamped, position);
        }
    };

    return (
        <div className="space-y-4">
            <Tabs value={mode} onValueChange={(value) => onModeChange(value as RatingMode)} className="w-full">
                <TabsList className="grid w-full grid-cols-2 bg-gray-100 p-1 rounded-xl">
                    <TabsTrigger value="detailed" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg text-sm">
                        ☕ Detailed
                    </TabsTrigger>
                    <TabsTrigger value="quick" className="data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-lg text-sm">
                        ⚡ Quick Rate
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="quick" className="mt-4">
                    <QuickRateControl
                        value={quickRate || 5}
                        onChange={onQuickRateChange}
                    />
                </TabsContent>
                <TabsContent value="detailed" className="mt-4">
                    {categoryConfigs.length > 0 ? (
                        <div className="space-y-5">
                            {categoryConfigs.map((config) => {
                                if (config.isCentered) {
                                    let currentPos = centeredSliderPositions[config.category];
                                    if (currentPos === undefined && rating[config.category]) {
                                        const ratingValue = rating[config.category];
                                        currentPos = ratingValue === 10 ? 0 : -Math.min((10 - ratingValue) / 2, config.maxRange || 5);
                                    } else if (currentPos === undefined) {
                                        currentPos = 0;
                                    }
                                    return (
                                        <CenteredDotRatingControl
                                            key={config.category}
                                            position={currentPos}
                                            onChange={handleCenteredChange(config.category)}
                                            leftLabel={config.leftLabel || ''}
                                            rightLabel={config.rightLabel || ''}
                                            label={config.category}
                                            maxRange={config.maxRange}
                                        />
                                    );
                                }

                                return (
                                    <DotRatingControl
                                        key={config.category}
                                        value={rating[config.category] || 0}
                                        onChange={(value) => handleRatingChange(config.category, value)}
                                        required={config.required}
                                        label={config.category}
                                        weight={config.weight}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-6 text-sm text-muted-foreground">
                            select a coffee type to see rating categories
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
};

